import { Dialog } from '@strata/react-dialog';
import { NumberInputFormik, TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { validationStrings } from '@strata/strata-ui-strings';
import { getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import {
    markdownLinksHelperText,
    StopPropagation,
    SubTaskConditionTypes,
    subtaskTypesList,
} from 'components/valueCase/helpers';

export const EditSubTaskDialog = ({ id = '', openSubTaskDialog = '', setOpenSubTaskDialog }) => {
    const formik = useFormikContext();
    const [initialContent, setInitialContent] = useState();

    const uniqueId =
        getIn(formik.values, `${id}.distinctId`) ?? getIn(formik.values, `${id}.subtask_id`);

    const isOpen = uniqueId === openSubTaskDialog;

    useEffect(() => {
        if (!initialContent && isOpen) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setInitialContent(getIn(formik.values, id));
        }
    }, [formik.values, id, initialContent, isOpen]);

    const errorsCheck = ({
        conditions: _conditions = undefined,
        subtask_type: subtaskTypeError = undefined,
        ...rest
    }) => {
        if (Object.keys(rest).length > 0) {
            return true;
        }
        // We want to restrict leaving for all but if the user has selected a different subtask type.
        return subtaskTypeError === validationStrings.requiredField.errorMessage;
    };
    return (
        <div {...StopPropagation}>
            <Dialog
                dialogTitle="Add/edit SubTask"
                isOpen={isOpen}
                secondaryButtonLabel="Cancel"
                onSecondaryButtonClick={async () => {
                    await formik.setFieldValue(id, initialContent);
                    setInitialContent();
                    setOpenSubTaskDialog();
                }}
                primaryButtonLabel="Save"
                onPrimaryButtonClick={() => {
                    setInitialContent();
                    setOpenSubTaskDialog();
                }}
                isPrimaryButtonDisabled={errorsCheck(getIn(formik.errors, id) || {})}
                width={1500}
                dialogContent={
                    <>
                        <div className="padding__bottom--medium">Define the subtask below.</div>
                        <TextInputFormik
                            id={`${id}.subtask_title`}
                            label="SubTask Name"
                            placeholderText="Enter Subtask Name"
                            required
                        />
                        <TextAreaFormik
                            id={`${id}.subtask_instructions`}
                            label="SubTask Instructions"
                            placeholderText="Provide Instructions for Subtask"
                            helperText={markdownLinksHelperText}
                            required
                        />
                        <span className="sds_performance__flex-row">
                            <RadioButtonGroupFormik
                                name={`${id}.subtask_type`}
                                label="Subtask Type"
                                helperText="Subtask type determines how the user will complete tasks. Choose whether the user will manually enter information or information will be automatically populated by the system."
                                required
                                options={subtaskTypesList}
                            />
                            {getIn(formik.values, `${id}.subtask_type`) ===
                                SubTaskConditionTypes.MANUAL && (
                                <>
                                    <NumberInputFormik
                                        id={`${id}.completion_days`}
                                        label="Days to Complete"
                                        placeholderText="0"
                                        helperText="Days after activation untill subtask is due."
                                        required
                                    />
                                    <NumberInputFormik
                                        id={`${id}.warning_threshold_days`}
                                        label="Warning Threshold Days"
                                        placeholderText="0"
                                        helperText="Days before due date when subtask enters warning status."
                                        required
                                    />
                                </>
                            )}
                        </span>
                    </>
                }
            />
        </div>
    );
};

EditSubTaskDialog.propTypes = {
    id: PropTypes.string,
    openSubTaskDialog: PropTypes.string,
    setOpenSubTaskDialog: PropTypes.func.isRequired,
};
